import {
  applySelectedTaskIdsToTasks,
  getSelectedTaskIdsFromTasks,
  getUniqueExistingTaskIds,
} from '../syberryTodayTaskSelectionUtils';
import {
  SyberryTodayPlanStatus,
  SyberryTodayPlanningStep,
  type SyberryTodayPlanning,
  type SyberryTodayTaskId,
  type SyberryTodayWorkItem,
} from '../types';
import type { SyberryTodayPlanningState, SyberryTodaySyncPlanningOptions } from './types';

export const getSyberryTodayPlanningStep = (status: SyberryTodayPlanStatus): SyberryTodayPlanningStep => {
  if (status === SyberryTodayPlanStatus.ORGANIZING) return SyberryTodayPlanningStep.ORGANIZE_TASKS;
  if (status === SyberryTodayPlanStatus.CONFIRMED) return SyberryTodayPlanningStep.CONFIRMED;
  return SyberryTodayPlanningStep.SELECT_TASKS;
};

const buildStateFromPlanning = (planning: SyberryTodayPlanning): SyberryTodayPlanningState => {
  const selectedTaskIds = planning.selectedTaskIds.length
    ? getUniqueExistingTaskIds(planning.tasks, planning.selectedTaskIds)
    : getSelectedTaskIdsFromTasks(planning.tasks);
  const tasks = applySelectedTaskIdsToTasks(planning.tasks, selectedTaskIds);

  return {
    planStatus: planning.status,
    projects: planning.projects,
    groups: planning.groups,
    streams: planning.streams,
    tasks,
    selectedTaskIds,
    expandedTaskId: null,
    activeGroupId: planning.groups[0]?.id ?? null,
    isSkipCheckInModalOpen: false,
  };
};

export const mergeStateWithPlanning = (
  state: SyberryTodayPlanningState,
  planning: SyberryTodayPlanning,
  options: SyberryTodaySyncPlanningOptions = {},
): SyberryTodayPlanningState => {
  const serverState = buildStateFromPlanning(planning);
  const activeGroupId = serverState.groups.some(group => group.id === state.activeGroupId)
    ? state.activeGroupId
    : (serverState.groups[0]?.id ?? null);
  const expandedTaskId = serverState.tasks.some(task => task.id === state.expandedTaskId) ? state.expandedTaskId : null;

  return {
    ...serverState,
    activeGroupId,
    expandedTaskId,
    isSkipCheckInModalOpen: options.closeSkipCheckInModal ? false : state.isSkipCheckInModalOpen,
  };
};

export const createEmptySyberryTodayPlanningState = (): SyberryTodayPlanningState => ({
  planStatus: SyberryTodayPlanStatus.NOT_STARTED,
  projects: [],
  groups: [],
  streams: [],
  tasks: [],
  selectedTaskIds: [],
  expandedTaskId: null,
  activeGroupId: null,
  isSkipCheckInModalOpen: false,
});

export const selectTasksByIds = (tasks: SyberryTodayWorkItem[], taskIds: SyberryTodayTaskId[]) => {
  const taskById = new Map(tasks.map(task => [task.id, task]));
  const selectedTasks: SyberryTodayWorkItem[] = [];

  taskIds.forEach(taskId => {
    const task = taskById.get(taskId);

    if (task) selectedTasks.push(task);
  });

  return selectedTasks;
};
